import React, { useState, useEffect, useRef } from 'react';
import { InlineWidget } from "react-calendly";

/* ─── Keyframe & global CSS ─────────────────────────────────────────── */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }
body {
  font-family: 'Plus Jakarta Sans', sans-serif;
  background: #f5fdf8;
  color: #0f1a13;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}

:root {
  --green:       #10b981;
  --green-deep:  #059669;
  --green-pale:  #ecfdf5;
  --green-ring:  rgba(16,185,129,0.15);
  --dark:        #0f1a13;
  --ink:         #1e3a2b;
  --muted:       #6b7f74;
  --faint:       #d1e8da;
  --white:       #ffffff;
  --bg:          #f5fdf8;
  --card:        #ffffff;
  --border:      #d6ede1;
}

/* ── Decorative circle motif (from brand video) ── */
.circle-deco {
  position: absolute;
  border-radius: 50%;
  border: 2.5px solid rgba(16,185,129,0.12);
  pointer-events: none;
}

/* ── Reveal ── */
.reveal {
  opacity: 0;
  transform: translateY(36px);
  transition: opacity .9s cubic-bezier(.16,1,.3,1), transform .9s cubic-bezier(.16,1,.3,1);
}
.reveal.visible { opacity: 1; transform: translateY(0); }
.delay-1 { transition-delay: .1s; }
.delay-2 { transition-delay: .2s; }
.delay-3 { transition-delay: .3s; }
.delay-4 { transition-delay: .4s; }
.delay-5 { transition-delay: .5s; }

/* ── Marquee ── */
@keyframes marquee {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
.marquee-track { animation: marquee 22s linear infinite; }

/* ── Float badge ── */
@keyframes float {
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-8px); }
}
.float-badge { animation: float 4s ease-in-out infinite; }
.float-badge-2 { animation: float 4s ease-in-out infinite; animation-delay: 1.2s; }

/* ── Pulse dot ── */
@keyframes pulse {
  0%,100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.6); opacity: .4; }
}
.pulse-dot::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 2px solid var(--green);
  animation: pulse 2s ease-in-out infinite;
}

/* ── Gradient text ── */
.grad-text {
  background: linear-gradient(135deg, #10b981 0%, #059669 60%, #047857 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── Glassy card ── */
.glass {
  background: rgba(255,255,255,.85);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255,255,255,.9);
}

/* ── Nav ── */
.nav {
  position: fixed; top: 0; width: 100%; z-index: 200;
  padding: 0 2rem;
  transition: all .4s;
}
.nav.scrolled {
  background: rgba(245,253,248,.9);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border);
  box-shadow: 0 1px 20px rgba(16,185,129,.06);
}
.nav-inner {
  max-width: 1280px; margin: 0 auto;
  height: 76px; display: flex; align-items: center; justify-content: space-between;
}
.logo { display: flex; align-items: center; gap: 10px; cursor: pointer; text-decoration: none; }
.logo-icon {
  width: 34px; height: 34px;
  background: var(--green);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(16,185,129,.35);
}
.logo-icon-inner { width: 12px; height: 12px; background: #fff; border-radius: 50%; }
.logo-name {
  font-family: 'Outfit', sans-serif;
  font-size: 1.45rem; font-weight: 800;
  color: var(--dark); letter-spacing: -.02em;
}
.nav-links { display: flex; align-items: center; gap: 2.5rem; list-style: none; }
.nav-links a {
  font-size: .8rem; font-weight: 600; letter-spacing: .06em;
  text-transform: uppercase; color: var(--muted); text-decoration: none;
  transition: color .2s;
}
.nav-links a:hover { color: var(--dark); }
.nav-cta {
  padding: .6rem 1.4rem;
  background: var(--dark); color: #fff !important;
  border-radius: 100px;
  font-size: .78rem !important; font-weight: 700 !important;
  letter-spacing: .04em !important;
  transition: all .25s !important;
  box-shadow: 0 4px 14px rgba(15,26,19,.15);
}
.nav-cta:hover { background: var(--green) !important; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(16,185,129,.3) !important; }
.hamburger { display: none; background: none; border: none; cursor: pointer; }
.mob-menu {
  display: none; background: var(--white);
  border-bottom: 1px solid var(--border);
  padding: 1.5rem 2rem;
  flex-direction: column; gap: 1.25rem;
}
.mob-menu.open { display: flex; }
.mob-menu a { font-size: .82rem; font-weight: 600; color: var(--ink); text-decoration: none; letter-spacing: .06em; text-transform: uppercase; }
@media(max-width:768px) { .nav-links { display: none; } .hamburger { display: block; } }

/* ── Buttons ── */
.btn-primary {
  display: inline-flex; align-items: center; gap: .5rem;
  padding: 1rem 2rem; background: var(--dark); color: #fff;
  font-family: 'Outfit', sans-serif; font-size: .9rem; font-weight: 700;
  border-radius: 100px; text-decoration: none; border: none; cursor: pointer;
  transition: all .28s; box-shadow: 0 6px 20px rgba(15,26,19,.2);
}
.btn-primary:hover { background: var(--green); transform: translateY(-2px); box-shadow: 0 12px 32px rgba(16,185,129,.32); }
.btn-outline {
  display: inline-flex; align-items: center; gap: .5rem;
  padding: 1rem 2rem; background: transparent;
  border: 1.5px solid var(--border); color: var(--ink);
  font-family: 'Outfit', sans-serif; font-size: .9rem; font-weight: 600;
  border-radius: 100px; text-decoration: none; cursor: pointer;
  transition: all .28s;
}
.btn-outline:hover { border-color: var(--green); color: var(--green); transform: translateY(-2px); }

/* ── Section ── */
.section { padding: 7rem 2rem; position: relative; overflow: hidden; }
.section-inner { max-width: 1280px; margin: 0 auto; }
.section-eyebrow {
  display: inline-flex; align-items: center; gap: .6rem;
  font-size: .72rem; font-weight: 700; letter-spacing: .18em;
  text-transform: uppercase; color: var(--green); margin-bottom: 1.25rem;
}
.section-eyebrow-dot { width: 6px; height: 6px; background: var(--green); border-radius: 50%; }
.section-title {
  font-family: 'Outfit', sans-serif;
  font-size: clamp(2.4rem, 5vw, 3.8rem);
  font-weight: 800; line-height: 1.1; letter-spacing: -.03em;
  color: var(--dark);
}
.section-sub {
  font-size: 1.05rem; font-weight: 300; color: var(--muted);
  line-height: 1.85; max-width: 520px;
}

/* ── HERO ── */
.hero {
  min-height: 100vh;
  display: flex; align-items: center;
  padding: 120px 2rem 80px;
  position: relative; overflow: hidden;
}
.hero-bg-circle-1 {
  position: absolute; width: 600px; height: 600px;
  top: -150px; right: -100px;
  border-radius: 50%;
  border: 2px solid rgba(16,185,129,.1);
  pointer-events: none;
}
.hero-bg-circle-2 {
  position: absolute; width: 900px; height: 900px;
  top: -300px; right: -300px;
  border-radius: 50%;
  border: 1.5px solid rgba(16,185,129,.06);
  pointer-events: none;
}
.hero-bg-circle-3 {
  position: absolute; width: 400px; height: 400px;
  bottom: -100px; left: -120px;
  border-radius: 50%;
  border: 2px solid rgba(16,185,129,.08);
  pointer-events: none;
}
.hero-glow {
  position: absolute; width: 500px; height: 500px;
  top: 0; right: 0;
  background: radial-gradient(circle, rgba(16,185,129,.08) 0%, transparent 70%);
  pointer-events: none;
}
.hero-inner {
  max-width: 1280px; margin: 0 auto; width: 100%;
  display: grid; grid-template-columns: 1.1fr 0.9fr;
  gap: 5rem; align-items: center;
}
@media(max-width:900px) { .hero-inner { grid-template-columns: 1fr; gap: 3rem; } }
.hero-tag {
  display: inline-flex; align-items: center; gap: .6rem;
  background: var(--green-pale);
  border: 1px solid rgba(16,185,129,.2);
  border-radius: 100px; padding: .4rem 1rem;
  font-size: .72rem; font-weight: 700; color: var(--green-deep);
  letter-spacing: .08em; text-transform: uppercase; margin-bottom: 1.75rem;
}
.hero-title {
  font-family: 'Outfit', sans-serif;
  font-size: clamp(3rem, 6.5vw, 5.2rem);
  font-weight: 900; line-height: 1.02;
  letter-spacing: -.04em; color: var(--dark);
  margin-bottom: 1.5rem;
}
.hero-sub {
  font-size: 1.1rem; font-weight: 300; color: var(--muted);
  line-height: 1.8; max-width: 480px; margin-bottom: 2.5rem;
}
.hero-actions { display: flex; align-items: center; gap: 1.25rem; flex-wrap: wrap; }
.hero-note { font-size: .8rem; color: var(--muted); }

/* Hero visual */
.hero-visual { position: relative; }
.hero-img-wrap { position: relative; }
.hero-img {
  width: 100%; aspect-ratio: 4/5; object-fit: cover;
  border-radius: 28px;
  box-shadow: 0 40px 80px rgba(15,26,19,.14);
}
.hero-img-overlay {
  position: absolute; inset: 0; border-radius: 28px;
  background: linear-gradient(to bottom, transparent 60%, rgba(15,26,19,.15));
  pointer-events: none;
}
/* Floating badges on hero image */
.hero-badge {
  position: absolute;
  background: var(--white);
  border-radius: 16px;
  padding: .85rem 1.1rem;
  box-shadow: 0 12px 40px rgba(15,26,19,.12);
  display: flex; align-items: center; gap: .65rem;
  border: 1px solid rgba(16,185,129,.12);
}
.hero-badge-icon {
  width: 36px; height: 36px; border-radius: 10px;
  background: var(--green-pale);
  display: flex; align-items: center; justify-content: center;
  color: var(--green);
}
.hero-badge-label { font-size: .65rem; font-weight: 700; color: var(--muted); letter-spacing: .06em; text-transform: uppercase; }
.hero-badge-value { font-family: 'Outfit', sans-serif; font-size: .9rem; font-weight: 800; color: var(--dark); }

/* ── Stats bar ── */
.stats-bar {
  background: var(--dark);
  padding: 1.75rem 2rem;
}
.stats-bar-inner {
  max-width: 1280px; margin: 0 auto;
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
}
@media(max-width:700px) { .stats-bar-inner { grid-template-columns: repeat(2,1fr); } }
.stat-item { text-align: center; }
.stat-num {
  font-family: 'Outfit', sans-serif;
  font-size: 2rem; font-weight: 900;
  color: var(--green); letter-spacing: -.03em; display: block;
}
.stat-label { font-size: .72rem; font-weight: 500; color: rgba(255,255,255,.5); letter-spacing: .08em; text-transform: uppercase; margin-top: .2rem; }

/* ── Problem section ── */
.problem-section { background: var(--white); }
.problem-inner {
  max-width: 1280px; margin: 0 auto;
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 6rem; align-items: center;
}
@media(max-width:900px) { .problem-inner { grid-template-columns: 1fr; gap: 3rem; } }
.problem-title {
  font-family: 'Outfit', sans-serif;
  font-size: clamp(2.2rem, 4vw, 3.5rem);
  font-weight: 900; letter-spacing: -.035em; line-height: 1.1;
  color: var(--dark); margin-bottom: 1.25rem;
}
.problem-sub { font-size: 1rem; color: var(--muted); line-height: 1.8; font-weight: 300; margin-bottom: 2rem; }
.pills { display: flex; flex-wrap: wrap; gap: .65rem; margin-bottom: 2rem; }
.pill {
  padding: .55rem 1.1rem;
  border-radius: 100px;
  font-size: .82rem; font-weight: 600;
  display: flex; align-items: center; gap: .4rem;
  border: 1.5px solid;
}
.pill-red { border-color: #fca5a5; color: #dc2626; background: #fef2f2; }
.pill-orange { border-color: #fcd34d; color: #d97706; background: #fffbeb; }
.pill-purple { border-color: #c4b5fd; color: #7c3aed; background: #f5f3ff; }
.problem-failure {
  font-family: 'Outfit', sans-serif;
  font-size: 1rem; font-weight: 700; color: var(--muted);
  padding: 1rem 1.5rem; background: #fef2f2;
  border-radius: 12px; border-left: 3px solid #dc2626;
  display: inline-block;
}
/* Problem visual - stacked cards */
.problem-visual { position: relative; height: 420px; }
.problem-card {
  position: absolute; width: 280px;
  background: var(--white);
  border-radius: 20px; padding: 1.5rem;
  box-shadow: 0 20px 60px rgba(15,26,19,.1);
  border: 1px solid var(--border);
}
.problem-card-1 { top: 0; left: 0; transform: rotate(-3deg); }
.problem-card-2 { top: 40px; left: 60px; transform: rotate(1.5deg); z-index: 1; }
.problem-card-3 { top: 80px; left: 120px; z-index: 2; background: #fff8f8; border-color: #fecaca; }
.problem-card-icon { font-size: 1.5rem; margin-bottom: .75rem; }
.problem-card-title { font-family: 'Outfit', sans-serif; font-size: 1rem; font-weight: 700; color: var(--dark); margin-bottom: .4rem; }
.problem-card-text { font-size: .82rem; color: var(--muted); line-height: 1.6; }

/* ── Solution / Built differently ── */
.solution-section { background: var(--bg); }
.solution-header { text-align: center; margin-bottom: 4rem; }
.solution-grid {
  display: grid; grid-template-columns: repeat(3,1fr);
  gap: 1.5rem;
}
@media(max-width:768px) { .solution-grid { grid-template-columns: 1fr; } }
.solution-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 24px; padding: 2.5rem 2rem;
  transition: all .3s;
  position: relative; overflow: hidden;
}
.solution-card::before {
  content: ''; position: absolute;
  top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, var(--green), var(--green-deep));
  transform: scaleX(0); transform-origin: left;
  transition: transform .4s;
}
.solution-card:hover { transform: translateY(-4px); box-shadow: 0 24px 60px rgba(16,185,129,.1); border-color: rgba(16,185,129,.2); }
.solution-card:hover::before { transform: scaleX(1); }
.solution-check {
  width: 48px; height: 48px; border-radius: 14px;
  background: var(--green); color: #fff;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 20px rgba(16,185,129,.3);
}
.solution-title { font-family: 'Outfit', sans-serif; font-size: 1.2rem; font-weight: 800; color: var(--dark); margin-bottom: .75rem; letter-spacing: -.02em; }
.solution-text { font-size: .9rem; color: var(--muted); line-height: 1.75; font-weight: 300; }

/* ── Services ── */
.services-section { background: var(--dark); }
.services-header { text-align: center; margin-bottom: 4rem; }
.services-header .section-eyebrow { color: var(--green); }
.services-header .section-title { color: #fff; }
.services-header .section-sub { color: rgba(255,255,255,.5); margin: 1rem auto 0; }
.services-grid {
  display: grid; grid-template-columns: repeat(3,1fr);
  gap: 1px; background: rgba(255,255,255,.08);
  border: 1px solid rgba(255,255,255,.08); border-radius: 24px; overflow: hidden;
}
@media(max-width:768px) { .services-grid { grid-template-columns: 1fr 1fr; } }
@media(max-width:480px) { .services-grid { grid-template-columns: 1fr; } }
.service-item {
  background: rgba(255,255,255,.03);
  padding: 2.5rem 2rem;
  transition: background .3s;
  cursor: default;
}
.service-item:hover { background: rgba(16,185,129,.08); }
.service-item-icon {
  width: 44px; height: 44px; border-radius: 12px;
  background: rgba(16,185,129,.15); border: 1px solid rgba(16,185,129,.2);
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 1.25rem; color: var(--green); font-size: 1.2rem;
}
.service-item-name { font-family: 'Outfit', sans-serif; font-size: 1.05rem; font-weight: 700; color: #fff; margin-bottom: .5rem; }
.service-item-desc { font-size: .82rem; color: rgba(255,255,255,.4); line-height: 1.65; }

/* ── Credibility ── */
.cred-section { background: var(--white); }
.cred-inner {
  max-width: 1280px; margin: 0 auto;
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 6rem; align-items: center;
}
@media(max-width:900px) { .cred-inner { grid-template-columns: 1fr; gap: 3rem; } }
.cred-quote {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.05rem; font-style: italic; font-weight: 300;
  color: var(--muted); line-height: 1.85;
  padding: 1.5rem 1.75rem;
  border-left: 3px solid var(--green);
  background: var(--green-pale); border-radius: 0 16px 16px 0;
  margin-top: 2rem;
}
.cred-logos { display: flex; gap: 1rem; flex-wrap: wrap; margin-top: 2.5rem; }
.cred-logo-pill {
  padding: .6rem 1.25rem;
  border-radius: 100px;
  font-family: 'Outfit', sans-serif;
  font-size: .88rem; font-weight: 800;
  letter-spacing: -.01em;
  border: 1.5px solid;
}
.cred-logo-bell   { color: #1d4ed8; border-color: #93c5fd; background: #eff6ff; }
.cred-logo-virgin { color: #dc2626; border-color: #fca5a5; background: #fef2f2; }
.cred-logo-siemens{ color: #047857; border-color: #6ee7b7; background: var(--green-pale); }
.cred-img {
  width: 100%; aspect-ratio: 1; object-fit: cover;
  border-radius: 28px; box-shadow: 0 40px 80px rgba(15,26,19,.1);
}

/* ── Pricing ── */
.pricing-section { background: var(--bg); }
.pricing-header { text-align: center; margin-bottom: 4rem; }
.pricing-grid {
  display: grid; grid-template-columns: repeat(3,1fr);
  gap: 1.5rem; align-items: start;
}
@media(max-width:900px) { .pricing-grid { grid-template-columns: 1fr; } }
.pricing-card {
  background: var(--white); border: 1px solid var(--border);
  border-radius: 24px; padding: 2.5rem;
  display: flex; flex-direction: column;
  transition: all .3s;
}
.pricing-card:hover { transform: translateY(-4px); box-shadow: 0 24px 60px rgba(16,185,129,.08); }
.pricing-card.featured {
  background: var(--dark); border-color: var(--dark);
  box-shadow: 0 24px 60px rgba(15,26,19,.2);
}
.pricing-badge {
  display: inline-block; padding: .3rem .85rem;
  background: var(--green); color: #fff;
  font-size: .65rem; font-weight: 800; letter-spacing: .1em;
  text-transform: uppercase; border-radius: 100px; margin-bottom: 1.5rem;
  align-self: flex-start;
}
.pricing-tier { font-size: .7rem; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--green); margin-bottom: .5rem; }
.pricing-name {
  font-family: 'Outfit', sans-serif; font-size: 1.5rem; font-weight: 800;
  letter-spacing: -.025em; color: var(--dark); margin-bottom: .75rem;
}
.pricing-card.featured .pricing-name { color: #fff; }
.pricing-desc { font-size: .88rem; color: var(--muted); line-height: 1.7; margin-bottom: 1.75rem; font-weight: 300; flex-grow: 1; }
.pricing-card.featured .pricing-desc { color: rgba(255,255,255,.5); }
.pricing-features { list-style: none; margin-bottom: 2rem; display: flex; flex-direction: column; gap: .7rem; }
.pricing-features li {
  font-size: .85rem; color: var(--ink);
  display: flex; align-items: center; gap: .6rem;
}
.pricing-card.featured .pricing-features li { color: rgba(255,255,255,.75); }
.pricing-features li::before {
  content: ''; width: 18px; height: 18px; flex-shrink: 0;
  border-radius: 50%; background: var(--green-pale);
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 8l3.5 3.5L13 5' stroke='%2310b981' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: center; background-size: 12px;
}
.pricing-card.featured .pricing-features li::before { background-color: rgba(16,185,129,.2); }
.btn-pricing-dark {
  padding: .9rem; border-radius: 100px;
  background: var(--dark); color: #fff;
  font-family: 'Outfit', sans-serif; font-size: .82rem; font-weight: 700;
  letter-spacing: .04em; text-decoration: none; display: block;
  text-align: center; border: none; cursor: pointer; transition: all .25s;
}
.btn-pricing-dark:hover { background: var(--green); }
.btn-pricing-green {
  padding: .9rem; border-radius: 100px;
  background: var(--green); color: #fff;
  font-family: 'Outfit', sans-serif; font-size: .82rem; font-weight: 700;
  letter-spacing: .04em; text-decoration: none; display: block;
  text-align: center; border: none; cursor: pointer; transition: all .25s;
  box-shadow: 0 8px 24px rgba(16,185,129,.35);
}
.btn-pricing-green:hover { background: var(--green-deep); transform: translateY(-1px); }

/* ── Big CTA ── */
.cta-section {
  background: var(--dark);
  text-align: center; padding: 8rem 2rem;
  position: relative; overflow: hidden;
}
.cta-glow {
  position: absolute; width: 800px; height: 800px;
  top: 50%; left: 50%; transform: translate(-50%,-50%);
  background: radial-gradient(circle, rgba(16,185,129,.12) 0%, transparent 65%);
  pointer-events: none;
}
.cta-title {
  font-family: 'Outfit', sans-serif;
  font-size: clamp(2.8rem, 6vw, 5rem);
  font-weight: 900; letter-spacing: -.04em; line-height: 1.05;
  color: #fff; margin-bottom: 1.25rem;
}
.cta-sub { font-size: 1.05rem; color: rgba(255,255,255,.5); font-weight: 300; margin-bottom: 2.5rem; }

/* ── Contact ── */
.contact-section { background: var(--bg); }
.contact-header { text-align: center; margin-bottom: 4rem; }
.contact-grid {
  display: grid; grid-template-columns: 5fr 7fr; gap: 2rem; align-items: start;
}
@media(max-width:1024px) { .contact-grid { grid-template-columns: 1fr; } }
.contact-form-card {
  background: var(--white); border: 1px solid var(--border);
  border-radius: 24px; padding: 2.5rem;
}
.form-title { font-family: 'Outfit', sans-serif; font-size: 1.4rem; font-weight: 800; color: var(--dark); letter-spacing: -.02em; margin-bottom: 2rem; }
.form-label { display: block; font-size: .68rem; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--muted); margin-bottom: .5rem; }
.form-input, .form-select, .form-textarea {
  width: 100%; background: var(--bg); border: 1.5px solid var(--border);
  border-radius: 12px; padding: .85rem 1rem;
  font-family: 'Plus Jakarta Sans', sans-serif; font-size: .9rem;
  color: var(--dark); outline: none; transition: border-color .2s;
  margin-bottom: 1.25rem; appearance: none;
}
.form-input::placeholder, .form-textarea::placeholder { color: var(--faint); }
.form-input:focus, .form-select:focus, .form-textarea:focus { border-color: var(--green); }
.form-textarea { resize: none; }
.form-select-wrap { position: relative; }
.form-select-wrap::after {
  content: '▾'; position: absolute; right: 1rem; top: 50%; transform: translateY(-60%);
  color: var(--muted); pointer-events: none; font-size: .8rem;
}
.btn-form {
  width: 100%; padding: 1rem; border-radius: 100px;
  background: var(--dark); color: #fff;
  font-family: 'Outfit', sans-serif; font-size: .9rem; font-weight: 800;
  border: none; cursor: pointer; transition: all .25s;
  letter-spacing: .02em;
}
.btn-form:hover { background: var(--green); }
.cal-card {
  background: var(--white); border: 1px solid var(--border);
  border-radius: 24px; overflow: hidden;
}
.cal-header { padding: 1.5rem 2rem; border-bottom: 1px solid var(--border); }
.cal-title { font-family: 'Outfit', sans-serif; font-size: 1.15rem; font-weight: 800; color: var(--dark); letter-spacing: -.02em; }
.cal-sub { font-size: .82rem; color: var(--muted); margin-top: .25rem; }
.direct-email { text-align: center; margin-top: 2.5rem; }
.email-a {
  display: inline-flex; align-items: center; gap: .5rem;
  font-size: 1rem; font-weight: 600; color: var(--green); text-decoration: none;
  transition: color .2s;
}
.email-a:hover { color: var(--green-deep); }

/* ── Footer ── */
footer {
  background: var(--dark); padding: 5rem 2rem 2.5rem;
}
.footer-inner {
  max-width: 1280px; margin: 0 auto;
  display: grid; grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 4rem;
}
@media(max-width:900px) { .footer-inner { grid-template-columns: 1fr 1fr; gap: 2.5rem; } }
@media(max-width:480px) { .footer-inner { grid-template-columns: 1fr; } }
.footer-tagline { font-size: .88rem; color: rgba(255,255,255,.4); line-height: 1.8; max-width: 280px; margin-top: 1.25rem; }
.footer-col-title { font-size: .65rem; font-weight: 700; letter-spacing: .18em; text-transform: uppercase; color: var(--green); margin-bottom: 1.25rem; }
.footer-col p, .footer-col a {
  font-size: .85rem; color: rgba(255,255,255,.4); line-height: 2;
  text-decoration: none; display: block; transition: color .2s;
}
.footer-col a:hover { color: rgba(255,255,255,.8); }
.footer-bottom {
  max-width: 1280px; margin: 3rem auto 0;
  padding-top: 2rem; border-top: 1px solid rgba(255,255,255,.08);
  display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem;
}
.footer-bottom p { font-size: .72rem; font-weight: 500; letter-spacing: .1em; text-transform: uppercase; color: rgba(255,255,255,.25); }

.hidden { display: none !important; }
`;

/* ─── App ─────────────────────────────────────────────────────────────── */
export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  const goHome = e => {
    if (e) e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.history.replaceState(null, null, window.location.pathname);
    setMobileOpen(false);
  };

  const [formStatus, setFormStatus] = useState('idle'); // idle | sending | success | error

  const handleSubmit = async e => {
    e.preventDefault();
    setFormStatus('sending');
    try {
      const res = await fetch('./contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: e.target.from_name.value,
          email: e.target.from_email.value,
          interest: e.target.interest.value,
          message: e.target.message.value
        })
      });
      if (res.ok) {
        setFormStatus('success');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M4 10l4.5 4.5L16 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const DashboardMockup = () => (
    <div style={{ position: 'relative', width: '100%', maxWidth: 480, margin: '0 auto' }}>

      {/* Main dashboard card */}
      <div style={{
        background: '#fff', borderRadius: 20, border: '1px solid #e2ede8',
        boxShadow: '0 32px 80px rgba(15,26,19,.13)',
        overflow: 'hidden', position: 'relative', zIndex: 2
      }}>
        {/* Window chrome */}
        <div style={{ background: '#f5fdf8', borderBottom: '1px solid #e2ede8', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {['#ff6058','#ffbd2e','#28ca42'].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: '50%', background: c }} />)}
          </div>
          <div style={{ flex: 1, background: '#e8f5ed', borderRadius: 6, height: 22, display: 'flex', alignItems: 'center', paddingLeft: 10 }}>
            <span style={{ fontSize: 10, color: '#6b7f74', fontWeight: 500 }}>app.operly.org/dashboard</span>
          </div>
        </div>

        {/* Dashboard body */}
        <div style={{ padding: '20px 20px 16px' }}>

          {/* Header row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <div>
              <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 800, fontSize: 15, color: '#0f1a13', letterSpacing: '-0.02em' }}>Operations Dashboard</div>
              <div style={{ fontSize: 11, color: '#6b7f74', marginTop: 2 }}>Monday, June 16 · All systems active</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#ecfdf5', borderRadius: 100, padding: '5px 10px', border: '1px solid rgba(16,185,129,.2)' }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', animation: 'pulse 2s infinite' }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: '#059669', letterSpacing: '.06em', textTransform: 'uppercase' }}>Live</span>
            </div>
          </div>

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 18 }}>
            {[
              { label: 'Tasks Done', val: '24', color: '#10b981', bg: '#ecfdf5' },
              { label: 'Emails Triaged', val: '91', color: '#6366f1', bg: '#eef2ff' },
              { label: 'Pending', val: '3', color: '#f59e0b', bg: '#fffbeb' },
            ].map(s => (
              <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 900, fontSize: 20, color: s.color, letterSpacing: '-.03em' }}>{s.val}</div>
                <div style={{ fontSize: 9, fontWeight: 600, color: '#6b7f74', letterSpacing: '.06em', textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Task list */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7f74', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>Today's Tasks</div>
            {[
              { task: 'Triage CEO inbox & flag urgent', done: true, tag: 'Inbox' },
              { task: 'Book Q3 offsite travel + hotels', done: true, tag: 'Travel' },
              { task: 'Update CRM with Tuesday leads', done: true, tag: 'CRM' },
              { task: 'Draft board meeting agenda', done: false, tag: 'Admin' },
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < 3 ? '1px solid #f0faf5' : 'none' }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  background: t.done ? '#10b981' : 'transparent',
                  border: t.done ? 'none' : '1.5px solid #d1e8da',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {t.done && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                <span style={{ fontSize: 12, color: t.done ? '#6b7f74' : '#0f1a13', fontWeight: t.done ? 400 : 500, textDecoration: t.done ? 'line-through' : 'none', flex: 1 }}>{t.task}</span>
                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 100, background: '#f0faf5', color: '#059669', flexShrink: 0 }}>{t.tag}</span>
              </div>
            ))}
          </div>

          {/* Inbox preview */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7f74', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>Inbox</div>
            {[
              { from: 'Sarah @ Acme', subject: 'Re: Proposal feedback', time: '9:14am', unread: true },
              { from: 'James (Investor)', subject: 'Call rescheduled ✓', time: '8:52am', unread: false },
            ].map((m, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10, background: m.unread ? '#f5fdf8' : 'transparent', marginBottom: 4 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: m.unread ? '#10b981' : '#e2ede8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: m.unread ? '#fff' : '#6b7f74' }}>{m.from[0]}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#0f1a13', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.from}</div>
                  <div style={{ fontSize: 10, color: '#6b7f74', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.subject}</div>
                </div>
                <span style={{ fontSize: 9, color: '#6b7f74', flexShrink: 0 }}>{m.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating badge — top left */}
      <div className="float-badge" style={{
        position: 'absolute', top: -20, left: -28, zIndex: 3,
        background: '#fff', borderRadius: 14, padding: '10px 14px',
        boxShadow: '0 12px 36px rgba(15,26,19,.12)', border: '1px solid rgba(16,185,129,.15)',
        display: 'flex', alignItems: 'center', gap: 10
      }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="16" height="16" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#6b7f74', letterSpacing: '.08em', textTransform: 'uppercase' }}>Onboarding</div>
          <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: 13, fontWeight: 800, color: '#0f1a13' }}>Within 24 hrs</div>
        </div>
      </div>

      {/* Floating badge — bottom right */}
      <div className="float-badge-2" style={{
        position: 'absolute', bottom: -18, right: -24, zIndex: 3,
        background: '#0f1a13', borderRadius: 14, padding: '10px 14px',
        boxShadow: '0 12px 36px rgba(15,26,19,.2)',
        display: 'flex', alignItems: 'center', gap: 10
      }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(16,185,129,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="16" height="16" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,.45)', letterSpacing: '.08em', textTransform: 'uppercase' }}>Replacement</div>
          <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff' }}>48hr Guarantee</div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

      {/* ── NAV ── */}
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-inner">
          <div className="logo" onClick={goHome}>
            <div className="logo-icon"><div className="logo-icon-inner" /></div>
            <span className="logo-name">Operly</span>
          </div>
          <ul className="nav-links">
            <li><a href="#problem">Why Operly</a></li>
            <li><a href="#services">Services</a></li>
            <li><a href="#pricing">Pricing</a></li>
            <li><a href="#contact" className="nav-cta">Book a Call</a></li>
          </ul>
          <button className="hamburger" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            <svg width="24" height="18" fill="none" viewBox="0 0 24 18">
              <rect y="0" width="24" height="2" rx="1" fill="#0f1a13"/>
              <rect y="8" width="18" height="2" rx="1" fill="#0f1a13"/>
              <rect y="16" width="12" height="2" rx="1" fill="#0f1a13"/>
            </svg>
          </button>
        </div>
        <div className={`mob-menu ${mobileOpen ? 'open' : ''}`}>
          <a href="#problem" onClick={() => setMobileOpen(false)}>Why Operly</a>
          <a href="#services" onClick={() => setMobileOpen(false)}>Services</a>
          <a href="#pricing" onClick={() => setMobileOpen(false)}>Pricing</a>
          <a href="#contact" onClick={() => setMobileOpen(false)} style={{ color: 'var(--green)' }}>Book a Call</a>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section id="home" className="hero">
        <div className="hero-bg-circle-1" />
        <div className="hero-bg-circle-2" />
        <div className="hero-bg-circle-3" />
        <div className="hero-glow" />

        <div className="hero-inner">
          {/* Left */}
          <div>
            <div className="hero-tag reveal">
              <span style={{ width: 6, height: 6, background: 'var(--green)', borderRadius: '50%', display: 'inline-block' }} />
              Canada-Based · Serving Globally
            </div>
            <h1 className="hero-title reveal delay-1">
              Stop gambling<br />
              with <span className="grad-text">freelancers.</span>
            </h1>
            <p className="hero-sub reveal delay-2">
              Operly is the fully managed VA agency built for founders who can't afford downtime. We own every outcome — not just every task.
            </p>
            <div className="hero-actions reveal delay-3">
              <a href="#contact" className="btn-primary">
                Book a Free Call
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </a>
              <a href="#problem" className="btn-outline">See How It Works</a>
            </div>
            <p className="hero-note reveal delay-4" style={{ marginTop: '1.25rem' }}>60-minute consultation · Completely free · No commitment</p>
          </div>

          {/* Right */}
          <div className="hero-visual reveal delay-2" style={{ padding: '2rem 3rem 2rem 1rem' }}>
            <DashboardMockup />
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div className="stats-bar">
        <div className="stats-bar-inner">
          {[
            { num: '48h', label: 'VA Replacement Guarantee' },
            { num: '24h', label: 'Onboarding Time' },
            { num: '6+', label: 'Years Founder Experience' },
            { num: '100%', label: 'Managed & Accountable' },
          ].map((s, i) => (
            <div className="stat-item" key={i}>
              <span className="stat-num">{s.num}</span>
              <span className="stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── VIDEO ── */}
      <section style={{ background: 'var(--white)', padding: '5rem 2rem' }}>
        <div className="section-inner">
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }} className="reveal">
            <div className="section-eyebrow" style={{ justifyContent: 'center' }}><span className="section-eyebrow-dot" />Our Story</div>
            <h2 className="section-title">See Operly in action.</h2>
          </div>
          <div className="reveal delay-1" style={{
            maxWidth: 860, margin: '0 auto',
            borderRadius: 24, overflow: 'hidden',
            boxShadow: '0 40px 80px rgba(15,26,19,.12)',
            border: '1px solid var(--border)',
            aspectRatio: '16/9', position: 'relative'
          }}>
            <iframe
              src="https://www.youtube.com/embed/9qaVYsBNg98"
              title="Operly — Built Differently"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <section id="problem" className="section problem-section">
        <div className="problem-inner section-inner">
          <div>
            <div className="section-eyebrow reveal"><span className="section-eyebrow-dot" />The Problem</div>
            <h2 className="problem-title reveal delay-1">Hiring one freelancer<br />is a gamble.</h2>
            <p className="problem-sub reveal delay-2">One person is one point of failure. When they're gone — your operations stop.</p>
            <div className="pills reveal delay-3">
              <span className="pill pill-red">😷 Sick days</span>
              <span className="pill pill-orange">⏰ No-shows</span>
              <span className="pill pill-purple">📅 Missed deadlines</span>
            </div>
            <div className="problem-failure reveal delay-4">"One person = one point of failure."</div>
          </div>

          <div className="reveal delay-2" style={{ position: 'relative', height: 400 }}>
            {/* Stacked chaos cards */}
            <div className="problem-card" style={{ position: 'absolute', top: 0, left: 20, transform: 'rotate(-4deg)', zIndex: 1 }}>
              <div className="problem-card-icon">😷</div>
              <div className="problem-card-title">VA is sick. Again.</div>
              <div className="problem-card-text">Your inbox is unmanaged. Clients are waiting. You're doing it yourself.</div>
            </div>
            <div className="problem-card" style={{ position: 'absolute', top: 50, left: 60, transform: 'rotate(2deg)', zIndex: 2 }}>
              <div className="problem-card-icon">👻</div>
              <div className="problem-card-title">No-show. No notice.</div>
              <div className="problem-card-text">The project is due. Your VA disappeared. No plan B exists.</div>
            </div>
            <div className="problem-card" style={{ position: 'absolute', top: 105, left: 100, zIndex: 3, background: '#fff8f8', borderColor: '#fecaca' }}>
              <div className="problem-card-icon">🔴</div>
              <div className="problem-card-title">Deadline missed.</div>
              <div className="problem-card-text">Because you relied on one person. That's the freelancer risk.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOLUTION ── */}
      <section className="section solution-section">
        <div className="section-inner">
          <div className="solution-header">
            <div className="section-eyebrow reveal" style={{ justifyContent: 'center' }}><span className="section-eyebrow-dot" />The Solution</div>
            <h2 className="section-title reveal delay-1">Operly is built differently.</h2>
            <p className="section-sub reveal delay-2" style={{ margin: '.75rem auto 0', textAlign: 'center' }}>
              We're not a freelancer marketplace. We're a fully managed support system with accountability built in.
            </p>
          </div>

          <div className="solution-grid">
            {[
              {
                title: 'Reliable, Fully Managed Support',
                text: 'Every VA is part of our team — trained, supervised, and backed by a full agency infrastructure. No freelancer risks.',
                delay: 'delay-1'
              },
              {
                title: 'Accountable — We Own Every Outcome',
                text: 'We don\'t just assign tasks. We own results. If something isn\'t done right, we fix it. That\'s the Operly standard.',
                delay: 'delay-2'
              },
              {
                title: 'VA Unavailable? Replaced in 48hrs',
                text: 'Life happens. When it does, we have someone ready. Zero downtime, zero dropped balls, zero explanations needed.',
                delay: 'delay-3'
              },
            ].map((s, i) => (
              <div className={`solution-card reveal ${s.delay}`} key={i}>
                <div className="solution-check"><CheckIcon /></div>
                <div className="solution-title">{s.title}</div>
                <p className="solution-text">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="section services-section">
        {/* Decorative circles matching brand video */}
        <div style={{ position: 'absolute', width: 300, height: 300, top: -80, right: -60, borderRadius: '50%', border: '2px solid rgba(16,185,129,.08)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', width: 200, height: 200, bottom: -40, left: -40, borderRadius: '50%', border: '2px solid rgba(16,185,129,.06)', pointerEvents: 'none' }} />

        <div className="section-inner">
          <div className="services-header">
            <div className="section-eyebrow reveal" style={{ justifyContent: 'center' }}><span className="section-eyebrow-dot" />What We Do</div>
            <h2 className="section-title reveal delay-1">We handle the busywork.</h2>
            <p className="section-sub reveal delay-2">So you can focus on the work that actually grows your business.</p>
          </div>

          <div className="services-grid reveal delay-2">
            {[
              { icon: '📧', name: 'Email & Inbox', desc: 'Fully managed inbox — triaged, replied to, and organised daily.' },
              { icon: '📅', name: 'Scheduling & Calendar', desc: 'No more back-and-forth. We own your calendar and protect your time.' },
              { icon: '🗂️', name: 'Admin Support', desc: 'Documents, reports, data entry — the behind-the-scenes that keeps you moving.' },
              { icon: '🎨', name: 'Social & Canva', desc: 'Content scheduling, graphic assets, and social media management.' },
              { icon: '📊', name: 'CRM & Data Entry', desc: 'Clean pipelines, accurate records, and follow-up that never slips.' },
              { icon: '✈️', name: 'Travel & Logistics', desc: 'Global travel arrangements, bookings, and event coordination.' },
            ].map((s, i) => (
              <div className="service-item" key={i}>
                <div className="service-item-icon">{s.icon}</div>
                <div className="service-item-name">{s.name}</div>
                <p className="service-item-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CREDIBILITY ── */}
      <section className="section cred-section">
        <div className="cred-inner section-inner">
          <div className="reveal">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=900"
              alt="Operly Team"
              className="cred-img"
            />
          </div>
          <div className="reveal delay-2">
            <div className="section-eyebrow"><span className="section-eyebrow-dot" />Why Trust Us</div>
            <h2 className="section-title" style={{ marginBottom: '1rem' }}>Every VA is trained<br />by our founder.</h2>
            <p className="section-sub" style={{ marginBottom: '1.75rem' }}>
              Our founder spent 6 years leading operations at some of the world's most demanding corporate environments.
            </p>
            <div className="cred-logos">
              <span className="cred-logo-pill cred-logo-bell">Bell</span>
              <span className="cred-logo-pill cred-logo-virgin">Virgin Media</span>
              <span className="cred-logo-pill cred-logo-siemens">Siemens</span>
            </div>
            <div className="cred-quote">
              "That corporate-grade standard goes into every client we serve. Not just the best practices — the discipline, the accountability, the follow-through."
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="section pricing-section">
        <div className="section-inner">
          <div className="pricing-header">
            <div className="section-eyebrow reveal" style={{ justifyContent: 'center' }}><span className="section-eyebrow-dot" />Retainer Plans</div>
            <h2 className="section-title reveal delay-1">Support Levels</h2>
            <p className="section-sub reveal delay-2" style={{ margin: '.75rem auto 0', textAlign: 'center' }}>
              Retainer-based. No surprises. Request our private pricing guide after your call.
            </p>
          </div>

          <div className="pricing-grid">
            <div className="pricing-card reveal delay-1">
              <div className="pricing-tier">Foundation</div>
              <div className="pricing-name">Essential Support</div>
              <p className="pricing-desc">Reliable daily operations for solo founders who need consistent, professional support.</p>
              <ul className="pricing-features">
                <li>Inbox Management</li>
                <li>Daily Scheduling</li>
                <li>Admin Tasks</li>
              </ul>
              <a href="#contact" className="btn-pricing-dark">Request Pricing</a>
            </div>

            <div className="pricing-card featured reveal delay-2" style={{ transform: 'translateY(-12px)' }}>
              <span className="pricing-badge">Most Popular</span>
              <div className="pricing-tier">Scale</div>
              <div className="pricing-name">Growth Support</div>
              <p className="pricing-desc">Deeply integrated partner for growing businesses with active client rotations.</p>
              <ul className="pricing-features">
                <li>Lead Nurture Flow</li>
                <li>CRM Ownership</li>
                <li>Social & Content</li>
                <li>Travel Management</li>
              </ul>
              <a href="#contact" className="btn-pricing-green">Get Quote</a>
            </div>

            <div className="pricing-card reveal delay-3">
              <div className="pricing-tier">Strategic Integration</div>
              <div className="pricing-name">Operations Partner</div>
              <p className="pricing-desc">Full integration for established businesses managing complex operational cycles.</p>
              <ul className="pricing-features">
                <li>Stakeholder Management</li>
                <li>Project Coordination</li>
                <li>Full Lifestyle Support</li>
              </ul>
              <a href="#contact" className="btn-pricing-dark">Request Pricing</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── BIG CTA ── */}
      <section className="cta-section">
        <div className="cta-glow" />
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Brand circles from video */}
          <div style={{ position: 'absolute', width: 400, height: 400, top: '50%', right: '-5%', transform: 'translateY(-50%)', borderRadius: '50%', border: '2px solid rgba(16,185,129,.1)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', width: 250, height: 250, bottom: '-10%', left: '-3%', borderRadius: '50%', border: '2px solid rgba(16,185,129,.08)', pointerEvents: 'none' }} />

          <h2 className="cta-title reveal">Ready to reclaim<br />your <span className="grad-text">headspace?</span></h2>
          <p className="cta-sub reveal delay-1">Book a free 60-minute call. No pitch. No pressure. Just a real conversation about your operations.</p>
          <div className="reveal delay-2" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <a href="#contact" className="btn-primary" style={{ background: 'var(--green)' }}>
              Book a Free Consultation
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
            <a href="mailto:admin@operly.org" className="btn-outline" style={{ borderColor: 'rgba(255,255,255,.15)', color: 'rgba(255,255,255,.7)' }}>
              admin@operly.org
            </a>
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="section contact-section">
        <div className="section-inner">
          <div className="contact-header">
            <div className="section-eyebrow reveal" style={{ justifyContent: 'center' }}><span className="section-eyebrow-dot" />Get In Touch</div>
            <h2 className="section-title reveal delay-1">Let's talk operations.</h2>
            <p className="section-sub reveal delay-2" style={{ margin: '.75rem auto 0', textAlign: 'center' }}>
              Send us a note or book directly into the calendar below.
            </p>
          </div>

          <div className="contact-grid reveal">
            <div className="contact-form-card">
              <div className="form-title">Send an Inquiry</div>
              {formStatus === 'success' ? (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                  <div style={{ width: 52, height: 52, background: 'var(--green)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', color: '#fff' }}>
                    <CheckIcon />
                  </div>
                  <h4 style={{ fontFamily: "'Outfit',sans-serif", fontSize: '1.2rem', fontWeight: 800, color: 'var(--dark)', marginBottom: '.4rem' }}>Message Sent!</h4>
                  <p style={{ fontSize: '.88rem', color: 'var(--muted)' }}>We'll be in touch within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <label className="form-label">Your Name</label>
                  <input type="text" name="from_name" required placeholder="Jane Smith" className="form-input" />
                  <label className="form-label">Email</label>
                  <input type="email" name="from_email" required placeholder="jane@company.com" className="form-input" />
                  <label className="form-label">Interested In</label>
                  <div className="form-select-wrap">
                    <select name="interest" className="form-select" style={{ marginBottom: '1.25rem' }}>
                      <option>General Inquiry</option>
                      <option>Pricing &amp; Packages</option>
                      <option>Free Consultation (60 mins)</option>
                      <option>Dedicated VA Support</option>
                    </select>
                  </div>
                  <label className="form-label">Current Bottleneck</label>
                  <textarea name="message" rows="4" required className="form-textarea" placeholder="What's eating your time right now?"></textarea>
                  {formStatus === 'error' && (
                    <p style={{ fontSize: '.82rem', color: '#dc2626', marginBottom: '1rem' }}>Something went wrong — please email us directly at admin@operly.org</p>
                  )}
                  <button type="submit" className="btn-form" disabled={formStatus === 'sending'}>
                    {formStatus === 'sending' ? 'Sending…' : 'Send Inquiry →'}
                  </button>
                </form>
              )}
            </div>

            <div className="cal-card">
              <div className="cal-header">
                <div className="cal-title">Book Directly</div>
                <p className="cal-sub">Pick a time for your free 30-minute discovery call.</p>
              </div>
              <InlineWidget
                url="https://calendly.com/jannatulferdousi0012/30min"
                styles={{ height: '700px', width: '100%', marginTop: '-50px' }}
                pageSettings={{
                  backgroundColor: 'ffffff',
                  hideEventTypeDetails: false,
                  hideLandingPageDetails: true,
                  primaryColor: '10b981',
                  textColor: '0f1a13'
                }}
              />
            </div>
          </div>

          <div className="direct-email">
            <p style={{ fontSize: '.82rem', color: 'var(--muted)', marginBottom: '.75rem' }}>Prefer email?</p>
            <a href="mailto:admin@operly.org" className="email-a">
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              admin@operly.org
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer>
        <div className="footer-inner">
          <div>
            <div className="logo" onClick={goHome}>
              <div className="logo-icon"><div className="logo-icon-inner" /></div>
              <span className="logo-name" style={{ color: '#fff' }}>Operly</span>
            </div>
            <p className="footer-tagline">Premium operational partnership for founders who value deep work over shallow admin.</p>
          </div>
          <div className="footer-col">
            <div className="footer-col-title">Navigate</div>
            <a href="#problem">Why Operly</a>
            <a href="#services">Services</a>
            <a href="#pricing">Pricing</a>
            <a href="#contact">Contact</a>
          </div>
          <div className="footer-col">
            <div className="footer-col-title">Region</div>
            <p>Headquartered in Canada.</p>
            <p>Serving Globally.</p>
          </div>
          <div className="footer-col">
            <div className="footer-col-title">Legal</div>
            <p>Confidentiality Assured.</p>
            <p>Cancel Anytime.</p>
            <p>Direct Partner Access.</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2025 Operly Operations Ltd.</p>
          <p style={{ fontStyle: 'italic' }}>Predictable Calm.</p>
        </div>
      </footer>
    </>
  );
}
